import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2, CheckCircle2, ClipboardList, LogOut, Plus, Search, ShieldCheck, UserPlus, Users
} from 'lucide-react'

import * as api from '@/lib/api.js'
import { useAuth } from '@/auth/AuthProvider.jsx'
import { Button } from '@/components/ui/button.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { Card, PageHeader } from '@/components/ui/card.jsx'
import { Field, Input } from '@/components/ui/field.jsx'
import { Alert, EmptyState, Spinner } from '@/components/ui/feedback.jsx'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog.jsx'
import { humanMessage } from '@/lib/errors.js'
import { cn, fullName } from '@/lib/utils.js'

const APPLICATION_STATUSES = ['new', 'reviewing', 'accepted', 'declined', 'onboarded', 'withdrawn']
const TENANT_TIERS = ['starter', 'growth', 'pro']
const TENANT_STATUSES = ['trial', 'active', 'past_due', 'suspended', 'canceled']
const SMS_STATUSES = ['not_configured', 'pending', 'active', 'disabled', 'failed']

// Phase 9 Part 1. Mirrors controllers/adminController.js PAYROLL_STATUSES and
// the tenants_payroll_status_check CHECK — narrower than SMS on purpose, since
// no provider can report a payroll failure back. See the migration.
const PAYROLL_STATUSES = ['not_enabled', 'pending', 'active']

// Order matters: subaccount first, because D37b makes it the field without
// which nothing sends, and it is the one a first-time operator will not
// expect to need.
const SMS_FIELDS = [
  {
    key: 'twilio_subaccount_sid',
    label: 'Subaccount SID',
    placeholder: 'AC...',
    hint: 'The tenant\'s own Twilio subaccount. Required before SMS can go active.'
  },
  { key: 'twilio_messaging_service_sid', label: 'Messaging Service SID', placeholder: 'MG...' },
  {
    key: 'twilio_from_number',
    label: 'From number',
    placeholder: '+15125550123',
    hint: 'Only used when there is no Messaging Service SID.'
  },
  { key: 'twilio_a2p_brand_sid', label: 'A2P Brand SID', placeholder: 'BN...' },
  { key: 'twilio_a2p_campaign_sid', label: 'A2P Campaign SID', placeholder: 'CM...' }
]

export function AdminConsole () {
  const [tab, setTab] = React.useState('applications')
  const { signOut } = useAuth()
  const admin = useQuery({ queryKey: ['admin-me'], queryFn: api.getAdminMe, retry: false })

  if (admin.isPending) {
    return <AdminFrame onSignOut={signOut}><Spinner label="Loading platform console" /></AdminFrame>
  }

  if (admin.error) {
    return (
      <AdminFrame onSignOut={signOut}>
        <Alert tone="danger" title="Platform access required">
          {humanMessage(admin.error)}
        </Alert>
      </AdminFrame>
    )
  }

  return (
    <AdminFrame activeTab={tab} onTab={setTab} onSignOut={signOut} admin={admin.data?.admin}>
      {tab === 'applications' ? <ApplicationsPanel /> : <TenantsPanel />}
    </AdminFrame>
  )
}

function AdminFrame ({ children, activeTab, onTab, onSignOut, admin }) {
  const nav = [
    { id: 'applications', label: 'Applications', icon: ClipboardList },
    { id: 'tenants', label: 'Tenants', icon: Building2 }
  ]

  return (
    <div className="min-h-dvh bg-canvas">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-line bg-surface lg:flex">
        <div className="flex h-16 items-center gap-3 border-b border-line px-5">
          <ShieldCheck className="size-5 text-brand" aria-hidden="true" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">MWS Console</p>
            <p className="truncate text-xs text-ink-muted">{admin?.email ?? 'Platform admin'}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3" aria-label="Platform">
          {nav.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => onTab?.(item.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors',
                activeTab === item.id
                  ? 'bg-brand-subtle text-brand'
                  : 'text-ink-muted hover:bg-surface-sunken hover:text-ink'
              )}
            >
              <item.icon className="size-4" aria-hidden="true" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-line p-3">
          <Button variant="ghost" className="w-full justify-start" onClick={onSignOut}>
            <LogOut /> Sign out
          </Button>
        </div>
      </aside>

      <header className="sticky top-0 z-30 border-b border-line bg-surface px-4 py-3 lg:hidden">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-brand" aria-hidden="true" />
            <span className="font-semibold text-ink">MWS Console</span>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onSignOut} aria-label="Sign out"><LogOut /></Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {nav.map(item => (
            <Button
              key={item.id}
              variant={activeTab === item.id ? 'primary' : 'secondary'}
              onClick={() => onTab?.(item.id)}
            >
              <item.icon /> {item.label}
            </Button>
          ))}
        </div>
      </header>

      <main className="px-4 pb-10 pt-5 sm:px-6 lg:ml-64 lg:pt-8">
        <div className="mx-auto w-full max-w-7xl">{children}</div>
      </main>
    </div>
  )
}

function ApplicationsPanel () {
  const qc = useQueryClient()
  const [status, setStatus] = React.useState('new')
  const [search, setSearch] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const apps = useQuery({
    queryKey: ['admin-applications', { status, search: debounced }],
    queryFn: () => api.getAdminApplications({
      status: status === 'all' ? undefined : status,
      search: debounced || undefined
    }),
    select: data => data.applications
  })

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250)
    return () => clearTimeout(t)
  }, [search])

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.updateAdminApplication(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-applications'] })
  })

  return (
    <>
      <PageHeader
        title="Applications"
        description="Public waitlist submissions from getmbos.com"
      />

      <div className="mb-4 grid gap-3 lg:grid-cols-[220px_1fr]">
        <Select value={status} onChange={setStatus} ariaLabel="Application status">
          <option value="all">All statuses</option>
          {APPLICATION_STATUSES.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </Select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            placeholder="Search company, contact, or email"
            aria-label="Search applications"
          />
        </div>
      </div>

      {apps.error && <Alert tone="danger" className="mb-4">{humanMessage(apps.error)}</Alert>}
      {update.error && <Alert tone="danger" className="mb-4">{humanMessage(update.error)}</Alert>}

      {apps.isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading applications" /></div>
        : apps.data.length === 0
          ? <EmptyState icon={ClipboardList} title="No applications" description="New waitlist submissions will appear here." />
          : (
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              <div className="hidden grid-cols-[90px_1.2fr_1fr_130px_180px] gap-3 border-b border-line bg-surface-sunken px-4 py-2 text-xs font-semibold uppercase text-ink-muted lg:grid">
                <span>Queue</span><span>Company</span><span>Contact</span><span>Status</span><span>Action</span>
              </div>
              <ul className="divide-y divide-line">
                {apps.data.map(application => (
                  <ApplicationRow
                    key={application.id}
                    application={application}
                    saving={update.isPending}
                    onStatus={next => update.mutate({ id: application.id, status: next })}
                    onNotes={notes => update.mutate({ id: application.id, notes })}
                  />
                ))}
              </ul>
            </div>
            )}
    </>
  )
}

function ApplicationRow ({ application, onStatus, onNotes, saving }) {
  const [notesOpen, setNotesOpen] = React.useState(false)
  const [notes, setNotes] = React.useState(application.notes ?? '')

  React.useEffect(() => setNotes(application.notes ?? ''), [application.notes])

  return (
    <li className="grid gap-3 px-4 py-3 lg:grid-cols-[90px_1.2fr_1fr_130px_180px] lg:items-center">
      <div className="text-sm tabular text-ink-muted">#{application.queue_position}</div>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink">{application.company}</p>
        <p className="truncate text-xs text-ink-muted">
          {[application.city, application.state, application.tier_interest].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm text-ink">{[application.first_name, application.last_name].filter(Boolean).join(' ')}</p>
        <p className="truncate text-xs text-ink-muted">{application.email}</p>
      </div>
      <Badge tone={statusTone(application.status)}>{label(application.status)}</Badge>
      <div className="flex flex-wrap gap-2">
        <Select value={application.status} onChange={onStatus} ariaLabel={`Status for ${application.company}`} disabled={saving}>
          {APPLICATION_STATUSES.map(s => <option key={s} value={s}>{label(s)}</option>)}
        </Select>
        <Button variant="secondary" size="sm" onClick={() => setNotesOpen(true)}>Notes</Button>
      </div>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent title={`Notes: ${application.company}`}>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="min-h-40 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand"
            aria-label="Application notes"
          />
          <DialogFooter className="-mx-5 -mb-4 mt-4">
            <Button variant="secondary" onClick={() => setNotesOpen(false)}>Cancel</Button>
            <Button
              loading={saving}
              onClick={() => {
                onNotes(notes)
                setNotesOpen(false)
              }}
            >
              Save notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  )
}

function TenantsPanel () {
  const qc = useQueryClient()
  const tenants = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: api.getAdminTenants,
    select: data => data.tenants
  })
  const [selectedId, setSelectedId] = React.useState(null)
  const [provisionOpen, setProvisionOpen] = React.useState(false)

  React.useEffect(() => {
    if (!selectedId && tenants.data?.[0]) setSelectedId(tenants.data[0].id)
  }, [selectedId, tenants.data])

  const selected = tenants.data?.find(t => t.id === selectedId) ?? tenants.data?.[0] ?? null

  return (
    <>
      <PageHeader
        title="Tenants"
        description={tenants.isPending ? 'Loading…' : `${tenants.data?.length ?? 0} workspace${tenants.data?.length === 1 ? '' : 's'}`}
        actions={<Button onClick={() => setProvisionOpen(true)}><UserPlus /> Provision tenant</Button>}
      />

      {tenants.error && <Alert tone="danger" className="mb-4">{humanMessage(tenants.error)}</Alert>}

      {tenants.isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading tenants" /></div>
        : tenants.data.length === 0
          ? <EmptyState icon={Building2} title="No tenants" description="Provision the first workspace from here." />
          : (
            <div className="grid gap-4 xl:grid-cols-[380px_1fr]">
              <Card className="overflow-hidden">
                <ul className="divide-y divide-line">
                  {tenants.data.map(tenant => (
                    <li key={tenant.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(tenant.id)}
                        className={cn(
                          'block w-full px-4 py-3 text-left hover:bg-surface-sunken',
                          selected?.id === tenant.id && 'bg-brand-subtle'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ink">{tenant.name}</p>
                            <p className="truncate text-xs text-ink-muted">{tenant.slug}</p>
                          </div>
                          <Badge tone={statusTone(tenant.status)}>{label(tenant.status)}</Badge>
                        </div>
                        <p className="mt-2 text-xs text-ink-muted">
                          {tenant.counts.clients} clients · {tenant.counts.jobs} jobs · {tenant.counts.members} users
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
              {selected && <TenantDetail tenant={selected} />}
            </div>
            )}

      <ProvisionDialog
        open={provisionOpen}
        onOpenChange={setProvisionOpen}
        onProvisioned={() => {
          qc.invalidateQueries({ queryKey: ['admin-tenants'] })
          qc.invalidateQueries({ queryKey: ['admin-applications'] })
        }}
      />
    </>
  )
}

function TenantDetail ({ tenant }) {
  const qc = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [creating, setCreating] = React.useState(false)
  const clients = useQuery({
    queryKey: ['admin-tenant-clients', tenant.id, search],
    queryFn: () => api.getAdminTenantClients(tenant.id, { search: search || undefined }),
    select: data => data.clients
  })

  const createClient = useMutation({
    mutationFn: body => api.createAdminTenantClient(tenant.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenant-clients', tenant.id] })
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
    }
  })

  return (
    <div className="space-y-4">
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">{tenant.name}</h2>
            <p className="text-sm text-ink-muted">{tenant.owner_email || 'No owner email'}</p>
          </div>
          <div className="flex gap-2">
            <Badge>{label(tenant.tier)}</Badge>
            <Badge tone={statusTone(tenant.status)}>{label(tenant.status)}</Badge>
          </div>
        </div>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <Stat label="Clients" value={tenant.counts.clients} />
          <Stat label="Jobs" value={tenant.counts.jobs} />
          <Stat label="Members" value={tenant.counts.members} />
        </dl>
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-xs font-semibold uppercase text-ink-muted">Domains</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {tenant.domains.map(domain => (
              <Badge key={domain.hostname} tone={domain.verification_status === 'verified' ? 'success' : 'warn'}>
                {domain.hostname}
              </Badge>
            ))}
          </div>
        </div>
      </Card>

      <SmsIdentityCard tenant={tenant} />

      <PayrollStatusCard tenant={tenant} />

      <MaverickWebhookCard tenant={tenant} />

      <Card className="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-ink">Clients</h3>
          <Button onClick={() => setCreating(true)}><Plus /> Add client</Button>
        </div>
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            placeholder="Search clients"
            aria-label="Search tenant clients"
          />
        </div>
        {clients.error && <Alert tone="danger" className="mb-4">{humanMessage(clients.error)}</Alert>}
        {clients.isPending
          ? <Spinner label="Loading clients" />
          : clients.data.length === 0
            ? <EmptyState icon={Users} title="No clients" description="Add one manually or wait for the tenant lead form to capture one." />
            : (
              <ul className="divide-y divide-line">
                {clients.data.map(client => (
                  <li key={client.id} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{fullName(client)}</p>
                      <p className="truncate text-xs text-ink-muted">{client.email || client.phone || 'No contact details'}</p>
                    </div>
                    <span className="text-xs text-ink-subtle">{[client.city, client.state].filter(Boolean).join(', ')}</span>
                  </li>
                ))}
              </ul>
              )}
      </Card>

      <ClientDialog
        open={creating}
        onOpenChange={setCreating}
        loading={createClient.isPending}
        error={createClient.error}
        onSubmit={body => createClient.mutate(body, { onSuccess: () => setCreating(false) })}
      />
    </div>
  )
}

/**
 * Tenant SMS identity (D37).
 *
 * Records what Rob already created by hand in the Twilio console. It does not
 * register anything: subaccount, brand and campaign creation stay manual per
 * D40, and this is where the resulting identifiers get attached to a tenant.
 *
 * None of these values is a credential (D38) — they are Twilio resource
 * identifiers, so they are shown in plain text on purpose.
 */
function SmsIdentityCard ({ tenant }) {
  const qc = useQueryClient()
  const [form, setForm] = React.useState(() => readSmsForm(tenant))
  const [testTo, setTestTo] = React.useState('')
  const [testResult, setTestResult] = React.useState(null)

  // Reset when the operator selects a different tenant, otherwise the form
  // would keep the previous tenant's SIDs and quietly save them onto this one.
  React.useEffect(() => {
    setForm(readSmsForm(tenant))
    setTestTo('')
    setTestResult(null)
  }, [tenant.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: body => api.updateAdminTenantSms(tenant.id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] })
  })

  const test = useMutation({
    mutationFn: () => api.sendAdminTenantTestSms(tenant.id, testTo.trim()),
    onSuccess: data => setTestResult(data)
  })

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))
  const status = tenant.twilio_sms_status ?? 'not_configured'

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">SMS (Twilio)</h3>
          <p className="text-xs text-ink-muted">
            Paste what Twilio gave you after the brand and campaign were approved.
          </p>
        </div>
        <Badge tone={smsTone(status)}>{label(status)}</Badge>
      </div>

      {save.error && <Alert tone="danger" className="mb-4">{humanMessage(save.error)}</Alert>}

      <form
        className="space-y-4"
        onSubmit={event => {
          event.preventDefault()
          save.mutate(form)
        }}
      >
        {SMS_FIELDS.map(field => (
          <Field
            key={field.key}
            id={`admin-sms-${field.key}`}
            label={field.label}
            value={form[field.key]}
            onChange={e => set(field.key, e.target.value)}
            placeholder={field.placeholder}
            hint={field.hint}
          />
        ))}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-ink-muted">Status</span>
            <Select
              value={form.twilio_sms_status}
              onChange={value => set('twilio_sms_status', value)}
              ariaLabel="SMS status"
            >
              {SMS_STATUSES.map(value => <option key={value} value={value}>{label(value)}</option>)}
            </Select>
          </div>
          <Button type="submit" loading={save.isPending}>Save SMS identity</Button>
        </div>
      </form>

      <div className="mt-5 border-t border-line pt-4">
        <p className="text-xs font-semibold uppercase text-ink-muted">Test send</p>
        <p className="mt-1 text-xs text-ink-muted">
          Sends one real SMS. Nothing is recorded against the tenant's documents.
        </p>
        {test.error && <Alert tone="danger" className="mt-3">{humanMessage(test.error)}</Alert>}
        {testResult && (
          <Alert tone="success" className="mt-3">
            Twilio accepted it{testResult.provider_status ? ` (${testResult.provider_status})` : ''}. Check the handset.
          </Alert>
        )}
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input
            type="tel"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            placeholder="+15125550123"
            aria-label="Test destination number"
          />
          <Button
            variant="secondary"
            loading={test.isPending}
            disabled={!testTo.trim() || status !== 'active'}
            onClick={() => test.mutate()}
          >
            Send test SMS
          </Button>
        </div>
        {status !== 'active' && (
          <p className="mt-2 text-xs text-ink-subtle">
            Set status to active and save before sending a test.
          </p>
        )}
      </div>
    </Card>
  )
}

/**
 * Tenant payroll gating (Phase 9 Part 1, D9.2).
 *
 * Deliberately the SMS card's shape, minus the half that cannot exist. There is
 * no field grid because there are no identifiers to record, and no test button
 * because SurePayroll has no API to call — the integration is a co-branded
 * signup link and nothing more (the finding recorded 2026-08-03).
 *
 * ── THE ONE THING THIS CARD MUST MAKE OBVIOUS ───────────────────────────────
 * That nothing verifies it. The SMS card's test-send is what turns "the console
 * says active" into evidence; here there is no such thing, so an operator could
 * flip this to Active for the wrong tenant and nothing anywhere would object.
 * The note under the control says so out loud rather than leaving the absence
 * to be discovered. It is a display gate, so the blast radius is a wrong screen
 * rather than a wrong charge — but "harmless when wrong" is a reason to state
 * the looseness, not to hide it.
 */
function PayrollStatusCard ({ tenant }) {
  const qc = useQueryClient()
  const status = tenant.payroll_status ?? 'not_enabled'
  const [value, setValue] = React.useState(status)

  // Same reset the SMS card needs, for the same reason: without it, selecting a
  // different tenant would leave the previous tenant's choice in the control
  // and a stray Save would write it onto this one.
  React.useEffect(() => { setValue(tenant.payroll_status ?? 'not_enabled') }, [tenant.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api.updateAdminTenantPayroll(tenant.id, { payroll_status: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] })
  })

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">Payroll (SurePayroll)</h3>
          <p className="text-xs text-ink-muted">
            Set this by hand once you have confirmed the tenant signed up.
          </p>
        </div>
        <Badge tone={payrollTone(status)}>{label(status)}</Badge>
      </div>

      {save.error && <Alert tone="danger" className="mb-4">{humanMessage(save.error)}</Alert>}

      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
        onSubmit={event => { event.preventDefault(); save.mutate() }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">Status</span>
          <Select value={value} onChange={setValue} ariaLabel="Payroll status">
            {PAYROLL_STATUSES.map(v => <option key={v} value={v}>{label(v)}</option>)}
          </Select>
        </div>
        <Button type="submit" loading={save.isPending}>Save payroll status</Button>
      </form>

      <p className="mt-4 border-t border-line pt-4 text-xs text-ink-subtle">
        Nothing verifies this. SurePayroll has no API and no webhook, so MBOS
        cannot detect a real signup and there is no test to run — unlike SMS
        above. Active only decides which screen the tenant sees under
        Operations &rsaquo; Payroll. It moves no money and stores no employee
        or tax data.
      </p>
    </Card>
  )
}

function payrollTone (status) {
  if (status === 'active') return 'success'
  if (status === 'pending') return 'warn'
  return 'neutral'
}

/**
 * Merchant transaction webhook (D41, build item 27(d)).
 *
 * The third card of this shape, and the one where the honesty note is not
 * politeness — it is the feature.
 *
 * ── HOW THIS DIFFERS FROM THE TWO ABOVE ─────────────────────────────────────
 * The SMS card can prove itself with a test send. The payroll card cannot, but
 * it only decides which screen a tenant sees. THIS card cannot be verified AND
 * it gates whether MBOS will call a workspace payment-ready. Getting it wrong
 * in the optimistic direction means a contractor takes real card payments while
 * MBOS shows every one of those invoices unpaid — the card clears, the customer
 * is happy, and nothing visibly breaks.
 *
 * So the copy states three things the operator cannot get from anywhere else:
 * that nothing verifies it, that the step happens in a portal MBOS has no
 * access to, and that recording it is what releases the `active` gate. The last
 * is the one an operator would otherwise learn by having a Session 2 activation
 * fail with a message about a trigger.
 *
 * ── WHY THERE IS NO "REGISTER WEBHOOK" BUTTON, EVER ─────────────────────────
 * Maverick, in writing 2026-08-07: webhooks "need to be created and configured
 * manually in the UI. We currently do not support being able to register or
 * manage Webhooks via API even when Connect access is granted." A future
 * session should not go looking for the endpoint. It was asked for directly and
 * the answer was no, including under Connect.
 */
function MaverickWebhookCard ({ tenant }) {
  const qc = useQueryClient()
  const state = tenant.maverick_webhook ?? { boarded: false, webhook_confirmed: false }
  const [value, setValue] = React.useState(state.webhook_confirmed ? 'confirmed' : 'not_confirmed')

  // The same reset the other two cards need, for the same reason.
  React.useEffect(() => {
    const next = tenant.maverick_webhook?.webhook_confirmed ? 'confirmed' : 'not_confirmed'
    setValue(next)
  }, [tenant.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api.updateAdminTenantMaverickWebhook(tenant.id, {
      webhook_confirmed: value === 'confirmed'
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-tenants'] })
  })

  const mavStatus = tenant.maverick_status ?? 'not_connected'
  const gateHolding = !state.webhook_confirmed && mavStatus !== 'active'

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">Merchant webhook (Maverick)</h3>
          <p className="text-xs text-ink-muted">
            Record this once the contractor has created the webhook in their own
            Maverick portal.
          </p>
        </div>
        <Badge tone={state.webhook_confirmed ? 'success' : 'warn'}>
          {state.webhook_confirmed ? 'Recorded' : 'Not recorded'}
        </Badge>
      </div>

      <dl className="mb-4 grid gap-3 text-sm sm:grid-cols-3">
        <Stat label="Payments status" value={label(mavStatus)} />
        <Stat label="Application" value={state.application_status ?? '—'} />
        <Stat label="Merchant ID" value={state.merchant_id ?? '—'} />
      </dl>

      {save.error && <Alert tone="danger" className="mb-4">{humanMessage(save.error)}</Alert>}

      {!state.boarded && (
        <Alert tone="info" className="mb-4">
          This workspace has no current payments application, so there is no
          merchant portal yet. Nothing to record until boarding starts.
        </Alert>
      )}

      <form
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
        onSubmit={event => { event.preventDefault(); save.mutate() }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">Webhook step</span>
          <Select
            value={value}
            onChange={setValue}
            ariaLabel="Merchant webhook step"
            disabled={!state.boarded}
          >
            <option value="not_confirmed">Not recorded</option>
            <option value="confirmed">Configured in portal</option>
          </Select>
        </div>
        <Button type="submit" loading={save.isPending} disabled={!state.boarded}>
          Save webhook step
        </Button>
      </form>

      {state.webhook_confirmed && state.webhook_confirmed_at && (
        <p className="mt-3 text-xs text-ink-muted">
          Recorded {new Date(state.webhook_confirmed_at).toLocaleString()}.
        </p>
      )}

      <p className="mt-4 border-t border-line pt-4 text-xs text-ink-subtle">
        Nothing verifies this. Maverick has no API to register, read or manage
        webhooks — explicitly including under Connect access — so MBOS cannot
        check whether the webhook exists, and there is no test to run. This
        records only that somebody says it was done.
        {' '}
        {gateHolding
          ? 'Until it is recorded, payments cannot be made active for this workspace.'
          : 'Recording it is what allows payments to be made active for this workspace.'}
        {' '}
        Getting it wrong in the optimistic direction is the expensive one: the
        tenant charges cards successfully while MBOS never hears about a single
        payment and shows their invoices unpaid.
      </p>
    </Card>
  )
}

function readSmsForm (tenant) {
  return {
    twilio_subaccount_sid: tenant.twilio_subaccount_sid ?? '',
    twilio_messaging_service_sid: tenant.twilio_messaging_service_sid ?? '',
    twilio_from_number: tenant.twilio_from_number ?? '',
    twilio_a2p_brand_sid: tenant.twilio_a2p_brand_sid ?? '',
    twilio_a2p_campaign_sid: tenant.twilio_a2p_campaign_sid ?? '',
    twilio_sms_status: tenant.twilio_sms_status ?? 'not_configured'
  }
}

function smsTone (status) {
  if (status === 'active') return 'success'
  if (status === 'pending') return 'warn'
  if (status === 'failed' || status === 'disabled') return 'danger'
  return 'neutral'
}

function ProvisionDialog ({ open, onOpenChange, onProvisioned }) {
  const [result, setResult] = React.useState(null)
  const [form, setForm] = React.useState({
    name: '',
    slug: '',
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    tier: 'starter',
    status: 'trial'
  })
  const provision = useMutation({
    mutationFn: api.provisionAdminTenant,
    onSuccess: data => {
      setResult(data)
      onProvisioned?.()
    }
  })

  React.useEffect(() => {
    if (open) {
      setResult(null)
      setForm({
        name: '',
        slug: '',
        owner_first_name: '',
        owner_last_name: '',
        owner_email: '',
        tier: 'starter',
        status: 'trial'
      })
      provision.reset()
    }
  }, [open])

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))

  function submit (event) {
    event.preventDefault()
    provision.mutate(form)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Provision tenant">
        {result
          ? (
            <div className="space-y-4">
              <Alert tone="success" title="Tenant provisioned">
                {result.tenant.name} is ready. Store the temporary password now if one was generated.
              </Alert>
              {result.temporary_password && (
                <div>
                  <p className="mb-2 text-sm font-medium text-ink">Temporary owner login</p>
                  <pre className="overflow-x-auto rounded-md border border-line bg-surface-sunken p-3 text-sm text-ink">
{`Email: ${result.owner.email}
Password: ${result.temporary_password}`}
                  </pre>
                </div>
              )}
              <DialogFooter className="-mx-5 -mb-4 mt-4">
                <Button onClick={() => onOpenChange(false)}><CheckCircle2 /> Done</Button>
              </DialogFooter>
            </div>
            )
          : (
            <form onSubmit={submit} className="space-y-4">
              {provision.error && <Alert tone="danger">{humanMessage(provision.error)}</Alert>}
              <Field id="tenant-name" label="Business name" value={form.name} onChange={e => set('name', e.target.value)} required />
              <Field id="tenant-slug" label="Slug" value={form.slug} onChange={e => set('slug', e.target.value)} hint="Optional" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="owner-first" label="Owner first name" value={form.owner_first_name} onChange={e => set('owner_first_name', e.target.value)} required />
                <Field id="owner-last" label="Owner last name" value={form.owner_last_name} onChange={e => set('owner_last_name', e.target.value)} />
              </div>
              <Field id="owner-email" label="Owner email" type="email" value={form.owner_email} onChange={e => set('owner_email', e.target.value)} required />
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-ink">
                  Tier
                  <Select value={form.tier} onChange={v => set('tier', v)} ariaLabel="Tier" className="mt-1">
                    {TENANT_TIERS.map(t => <option key={t} value={t}>{label(t)}</option>)}
                  </Select>
                </label>
                <label className="block text-sm font-medium text-ink">
                  Status
                  <Select value={form.status} onChange={v => set('status', v)} ariaLabel="Status" className="mt-1">
                    {TENANT_STATUSES.map(s => <option key={s} value={s}>{label(s)}</option>)}
                  </Select>
                </label>
              </div>
              <DialogFooter className="-mx-5 -mb-4 mt-4">
                <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button type="submit" loading={provision.isPending} disabled={!form.name.trim() || !form.owner_first_name.trim() || !form.owner_email.trim()}>
                  Provision
                </Button>
              </DialogFooter>
            </form>
            )}
      </DialogContent>
    </Dialog>
  )
}

function ClientDialog ({ open, onOpenChange, loading, error, onSubmit }) {
  const [form, setForm] = React.useState({ first_name: '', last_name: '', email: '', phone: '', city: '', state: '', notes: '' })

  React.useEffect(() => {
    if (open) setForm({ first_name: '', last_name: '', email: '', phone: '', city: '', state: '', notes: '' })
  }, [open])

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))
  const contactMissing = !form.email.trim() && !form.phone.trim()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add client">
        <form
          id="admin-client-form"
          className="space-y-4"
          onSubmit={event => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          {error && <Alert tone="danger">{humanMessage(error)}</Alert>}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="admin-client-first" label="First name" value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
            <Field id="admin-client-last" label="Last name" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
          </div>
          <Field id="admin-client-email" label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          <Field id="admin-client-phone" label="Phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} hint={contactMissing ? 'Email or phone is required.' : undefined} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="admin-client-city" label="City" value={form.city} onChange={e => set('city', e.target.value)} />
            <Field id="admin-client-state" label="State" value={form.state} onChange={e => set('state', e.target.value)} maxLength={2} />
          </div>
          <Field id="admin-client-notes" label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </form>
        <DialogFooter className="-mx-5 -mb-4 mt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form="admin-client-form" loading={loading} disabled={!form.first_name.trim() || contactMissing}>Add client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Select ({ value, onChange, children, ariaLabel, className, disabled = false }) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      aria-label={ariaLabel}
      className={cn(
        'h-10 rounded-md border border-line bg-surface px-3 text-sm text-ink outline-none focus:border-brand disabled:opacity-60',
        className
      )}
    >
      {children}
    </select>
  )
}

function Stat ({ label: statLabel, value }) {
  return (
    <div className="rounded-md bg-surface-sunken p-3">
      <dt className="text-xs text-ink-muted">{statLabel}</dt>
      <dd className="mt-1 text-lg font-semibold tabular text-ink">{value}</dd>
    </div>
  )
}

function label (value) {
  return String(value ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function statusTone (status) {
  if (['accepted', 'onboarded', 'active', 'trial'].includes(status)) return 'success'
  if (['reviewing', 'past_due'].includes(status)) return 'warn'
  if (['declined', 'withdrawn', 'suspended', 'canceled'].includes(status)) return 'danger'
  return 'neutral'
}
